import type { McpResourceContextUpdateRequest } from '@common/api/mcpConnectorService';

const SUPABASE_MCP_HOST = 'mcp.supabase.com';
const SUPABASE_PROJECT_REF_PATTERN = /^[a-z0-9]{16,32}$/;

export const isSupabaseMcpServerUrl = (serverUrl: string | null | undefined): boolean => {
    try {
        return new URL(serverUrl || '').hostname === 'mcp.supabase.com';
    } catch {
        return false;
    }
};

export const normalizeSupabaseProjectRef = (value: string): string => value.trim().toLowerCase();

export const isValidSupabaseProjectRef = (value: string): boolean => value === '' || SUPABASE_PROJECT_REF_PATTERN.test(value);

export const buildSupabaseResourceContextUpdate = (projectRef: string): McpResourceContextUpdateRequest => ({
    resourceContext: projectRef ? { project_id: projectRef } : {},
});

export interface McpResourceContextUpdateService<T> {
    updateResourceContext: (
        workspaceId: string,
        connectorId: number | string,
        request: McpResourceContextUpdateRequest,
    ) => Promise<T>;
}

export const saveSupabaseResourceContext = <T>({
    service,
    workspaceId,
    connectorId,
    projectRef,
}: {
    service: McpResourceContextUpdateService<T>;
    workspaceId: string;
    connectorId: number | string;
    projectRef: string;
}): Promise<T> => service.updateResourceContext(
    workspaceId,
    connectorId,
    buildSupabaseResourceContextUpdate(projectRef),
);

export type SupabaseResourceContextSaveResult<T> =
    | { kind: 'saved'; connector: T; refreshError?: unknown }
    | { kind: 'stale' };

export const saveAndRefreshSupabaseResourceContext = async <T>({
    service,
    workspaceId,
    connectorId,
    projectRef,
    isCurrentWorkspace,
    refresh,
}: {
    service: McpResourceContextUpdateService<T>;
    workspaceId: string;
    connectorId: number | string;
    projectRef: string;
    isCurrentWorkspace: () => boolean;
    refresh: () => Promise<unknown>;
}): Promise<SupabaseResourceContextSaveResult<T>> => {
    const connector = await saveSupabaseResourceContext({
        service,
        workspaceId,
        connectorId,
        projectRef,
    });
    if (!isCurrentWorkspace()) {
        return { kind: 'stale' };
    }

    try {
        await refresh();
    } catch (refreshError) {
        return isCurrentWorkspace()
            ? { kind: 'saved', connector, refreshError }
            : { kind: 'stale' };
    }

    return isCurrentWorkspace() ? { kind: 'saved', connector } : { kind: 'stale' };
};
